import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import type { CompanyWithRole, CompanyCreate } from "../types";
import { listCompanies, createCompany as apiCreate } from "../api/companies";
import apiClient from "../api/client";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "active_company_id";

interface CompanyState {
  companies: CompanyWithRole[];
  activeCompany: CompanyWithRole | null;
  isLoading: boolean;
  setActiveCompany: (company: CompanyWithRole) => void;
  createCompany: (data: CompanyCreate) => Promise<CompanyWithRole>;
  reload: () => Promise<void>;
}

const CompanyContext = createContext<CompanyState | null>(null);

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [companies, setCompanies] = useState<CompanyWithRole[]>([]);
  const [activeCompany, setActiveCompanyState] = useState<CompanyWithRole | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const reload = useCallback(async () => {
    setIsLoading(true);
    try {
      const list = await listCompanies();
      setCompanies(list);
      // Restore previously selected company
      const savedId = localStorage.getItem(STORAGE_KEY);
      const saved = list.find((c) => c.id === savedId);
      setActiveCompanyState(saved ?? list[0] ?? null);
    } catch {
      // ignore (user might not be authenticated yet)
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      reload();
    } else {
      setCompanies([]);
      setActiveCompanyState(null);
    }
  }, [isAuthenticated, reload]);

  // Inject X-Company-ID header on every request
  useEffect(() => {
    const id = apiClient.interceptors.request.use((config) => {
      if (activeCompany) {
        config.headers["X-Company-ID"] = activeCompany.id;
      }
      return config;
    });
    return () => apiClient.interceptors.request.eject(id);
  }, [activeCompany]);

  const setActiveCompany = useCallback((company: CompanyWithRole) => {
    setActiveCompanyState(company);
    localStorage.setItem(STORAGE_KEY, company.id);
  }, []);

  const createCompany = useCallback(
    async (data: CompanyCreate) => {
      const company = await apiCreate(data);
      await reload();
      setActiveCompany(company);
      return company;
    },
    [reload, setActiveCompany]
  );

  return (
    <CompanyContext.Provider
      value={{ companies, activeCompany, isLoading, setActiveCompany, createCompany, reload }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany(): CompanyState {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used inside CompanyProvider");
  return ctx;
}
